import { createStore } from 'vuex';
import api from '@/utils/axios';

let pingInterval: number | null = null;

function startPing() {
  if (pingInterval) clearInterval(pingInterval);

  pingInterval = window.setInterval(() => {
    api
      .post('auth/ping-session/', {}, { skipLoader: true, isPing: true } as any)
      .catch(() => {
        // ignore silently
      });
  }, 2 * 60 * 1000);
}

function stopPing() {
  if (pingInterval) clearInterval(pingInterval);
  pingInterval = null;
}

export default createStore({
  state: {
    access: null as string | null, // ✅ memory only
    user: null as any,
    isAuthenticated: false,
    hasLicenseSession: false,
    organization: null as any,
    peppolActive: false,
    loading: false,
    appReady: false,
    isInitializing: false,
  },

  mutations: {
    SET_TOKEN(state, access: string) {
      state.access = access;
      state.isAuthenticated = true;

      // Save token in browser storage
      localStorage.setItem('access', access);

      // Attach token to Axios
      api.defaults.headers.common.Authorization = `Bearer ${access}`;
    },

    CLEAR_TOKENS(state) {
      state.access = null;
      state.user = null;
      state.isAuthenticated = false;
      state.hasLicenseSession = false;

      // Remove token from storage
      localStorage.removeItem('access');

      delete api.defaults.headers.common.Authorization;
      stopPing();
    },

    SET_USER(state, user) {
      state.user = user;
    },
    SET_ORGANIZATION(state, org) {
      state.organization = org;
    },

    SET_HAS_LICENSE_SESSION(state, value: boolean) {
      state.hasLicenseSession = !!value;
      if (state.hasLicenseSession) startPing();
      else stopPing();
    },
    setPeppolActive(state, value) {
      state.peppolActive = value;
    },
    SET_LOADING(state, value: boolean) {
      state.loading = value;
    },
    SET_APP_READY(state, value: boolean) {
      state.appReady = value;
    },
    SET_INITIALIZING(state, value: boolean) {
      state.isInitializing = value;
    },
  },

  getters: {
    isLogged: (s) => s.isAuthenticated,
    currentUser: (s) => s.user,
  },

  actions: {
    async logIn({ commit, dispatch }, credentials) {
      commit('SET_INITIALIZING', true);

      const { data } = await api.post('auth/jwt/create/', credentials);

      commit('SET_TOKEN', data.access);

      await dispatch('fetchCurrentUser');

      try {
        await api.post('auth/login-license/');
        commit('SET_HAS_LICENSE_SESSION', true);

        await dispatch('fetchOrganization');

        commit('SET_APP_READY', true);
      } finally {
        commit('SET_INITIALIZING', false);
      }
    },

    async logOut({ commit }) {
      try {
        await api.post('auth/logout-session/');
      } catch {
        // ignore
      } finally {
        commit('CLEAR_TOKENS');
      }
    },

    async fetchCurrentUser({ commit }) {
      try {
        const res = await api.get('auth/users/me/');
        console.log('Current user:', res.data);
        commit('SET_USER', res.data);
        return true;
      } catch (err) {
        console.error('Failed to fetch current user', err);
        commit('SET_USER', null);
        return false;
      }
    },
    async fetchOrganization({ commit }) {
      try {
        const res = await api.get('/organizations/me/active/');

        console.log('ORG API:', res.data);

        commit('SET_ORGANIZATION', res.data);
      } catch (error) {
        console.error('❌ Failed to fetch organization', error);
      }
    },
    async fetchPeppolStatus({ commit }) {
      const res = await api.get('/organizations/me/peppol/status/');
      commit('setPeppolActive', res.data.peppol_active);
    },

    async restoreSession({ commit, dispatch }) {
      console.log('🔁 RESTORE SESSION START');

      const token = localStorage.getItem('access');

      if (!token) {
        console.log('❌ NO TOKEN');
        commit('SET_APP_READY', true); // no token, but app is ready to show login page
        return;
      }

      commit('SET_TOKEN', token);

      try {
        await dispatch('fetchCurrentUser');
        console.log('✅ USER OK');

        await api.post('auth/login-license/');
        console.log('✅ LICENSE OK');

        commit('SET_HAS_LICENSE_SESSION', true);

        await dispatch('fetchOrganization');
        console.log('✅ ORG OK');

        await dispatch('fetchPeppolStatus');

        console.log('✅ APP READY FROM RESTORE');
        commit('SET_APP_READY', true);
      } catch (err) {
        console.log('❌ RESTORE FAILED', err);
        commit('CLEAR_TOKENS');
      }
    },
  },
});
